import React, { useState } from 'react';
import { Pencil, Trash2, User, Phone, Mail, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { formatPacificDateTime } from '../../utils/dateTime';
import { DeletePatientLeadModal } from '../DeletePatientLeadModal';
import { PatientLeadStatusBadge } from './PatientLeadStatusBadge';

interface PatientLead {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  email: string | null;
  comments: string | null;
  mapped_patient_id: string | null;
  status: string;
  created_at: string;
  created_by: string;
  progress_percentage?: number;
}

interface PatientLeadsListProps {
  leads: PatientLead[];
  onEditLead: (lead: PatientLead) => void;
  onDeleteLead: (id: string) => void;
  onLeadClick: (lead: PatientLead) => void;
  isDeleting: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange: (field: string) => void;
}

export function PatientLeadsList({ 
  leads, 
  onEditLead, 
  onDeleteLead, 
  onLeadClick,
  isDeleting,
  sortField = 'created_at',
  sortDirection = 'desc',
  onSortChange
}: PatientLeadsListProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<PatientLead | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, lead: PatientLead) => {
    e.stopPropagation();
    setLeadToDelete(lead);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (leadToDelete) {
      onDeleteLead(leadToDelete.id);
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };

  // Format date and time separately
  const formatCreatedAt = (dateTimeString: string) => {
    const dateTime = new Date(dateTimeString);
    return {
      date: dateTime.toLocaleDateString(),
      time: dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1" />
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  // Handle sort click
  const handleSortClick = (field: string) => {
    onSortChange(field);
  };

  // Function to render comments preview
  const renderCommentsPreview = (comments: string | null) => {
    if (!comments) return <span className="text-gray-400">No comments</span>;
    
    // Strip HTML tags for preview
    const strippedComments = comments.replace(/<[^>]*>?/gm, '');
    
    // Limit to 50 characters
    const preview = strippedComments.length > 50 
      ? strippedComments.substring(0, 50) + '...' 
      : strippedComments;
      
    return preview;
  };

  // Function to render tooltip content with HTML formatting
  const renderTooltipContent = (comments: string | null) => {
    if (!comments) return null;
    
    // Check if the comments appear to contain HTML
    const containsHtml = /<[a-z][\s\S]*>/i.test(comments);
    
    if (containsHtml) {
      return <div dangerouslySetInnerHTML={{ __html: comments }} />;
    } else {
      // If it's plain text, preserve line breaks
      return (
        <div>
          {comments.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < comments.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      );
    }
  };

  return (
    <>
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer"
                onClick={() => handleSortClick('name')}
              >
                <div className="flex items-center">
                  Name
                  {renderSortIndicator('name')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Contact
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Comments
              </th>
              <th 
                scope="col" 
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24 cursor-pointer"
                onClick={() => handleSortClick('created_at')}
              >
                <div className="flex items-center">
                  Created
                  {renderSortIndicator('created_at')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                onClick={() => handleSortClick('progress')}
              >
                <div className="flex items-center">
                  Progress
                  {renderSortIndicator('progress')}
                </div>
              </th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {leads.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-10 text-center text-gray-500">
                  No patient leads found
                </td>
              </tr>
            ) : (
              leads.map((lead) => {
                const createdAt = formatCreatedAt(lead.created_at);
                
                return (
                  <tr 
                    key={lead.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onLeadClick(lead)}
                  >
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div>{lead.first_name} {lead.last_name}</div>
                          <div className="mt-1">
                            <PatientLeadStatusBadge status={lead.status} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="space-y-1">
                        {lead.phone_number && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 text-gray-400 mr-1" />
                            <span>{lead.phone_number}</span>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-gray-400 mr-1" />
                            <span>{lead.email}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500 relative">
                      {lead.comments ? (
                        <div 
                          className="flex items-start"
                          onMouseEnter={() => setTooltipVisible(lead.id)}
                          onMouseLeave={() => setTooltipVisible(null)}
                        >
                          <MessageSquare className="h-4 w-4 text-gray-400 mr-1 mt-0.5" />
                          <span className="line-clamp-2">{renderCommentsPreview(lead.comments)}</span>
                          
                          {/* Tooltip */}
                          {tooltipVisible === lead.id && lead.comments && (
                            <div className="absolute z-50 left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700 prose prose-sm max-w-none">
                              {renderTooltipContent(lead.comments)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No comments</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-24">
                      <div className="flex flex-col">
                        <span>{createdAt.date}</span>
                        <span className="text-xs text-gray-400">{createdAt.time}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-green-600 h-2.5 rounded-full" 
                          style={{ width: `${lead.progress_percentage || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        {lead.progress_percentage || 0}% Complete
                      </span>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditLead(lead);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(e, lead)}
                          className="text-red-600 hover:text-red-900"
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {leadToDelete && (
        <DeletePatientLeadModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          patientName={`${leadToDelete.first_name} ${leadToDelete.last_name}`}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}